// Smart Links — mounted under the xNotify Customer Portal's /marketing/smart-links/*.
//
// Per docs/open-items.md #11 (REVISED 2026-05-28 — second revision):
// (1) The page must run the CURRENT main of the Smart Links portal —
//     a copy of that portal's source lives in ./portal/ and is the single
//     source of truth for the UI. To take the latest, copy that tree from
//     the Smart Links portal repo's src/{components,lib,mocks,pages,store,types}
//     into ./portal/. Done by `npm run sync-smart-links` (TBD).
// (2) Auth piggybacks on the App's existing session — baseQuery reads
//     `api-key` from localStorage and forwards it as x-api-key.
// (3) A SCOPED Redux Provider hosts only the portal's RTK Query slice, so
//     the rest of the App is unaffected (App's main Redux store stays
//     unused as it is today).
//
// React Router nested routes mirror the portal's: LinksList + LinkDetail.

import React from 'react';
import { Provider } from 'react-redux';
import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, Center, ChakraProvider, extendTheme, Text, Spinner } from '@chakra-ui/react';
import Header from '../../components/Header';
import usePagePermissions from '../../hooks/usePagePermissions';

import { linksApi } from './portal/store/linksApi';
import { LinksListPage } from './portal/pages/LinksListPage';
import { LinkDetailPage } from './portal/pages/LinkDetailPage';
import { ActivityPage } from './portal/pages/ActivityPage';
import CustomDomainPanel from './CustomDomainPanel';

// Scoped store — only the linksApi reducer + middleware. App's main store
// remains independent so existing pages aren't affected.
const smartLinksStore = configureStore({
    reducer: { [linksApi.reducerPath]: linksApi.reducer },
    middleware: (getDefault) => getDefault().concat(linksApi.middleware),
});
setupListeners(smartLinksStore.dispatch);

// Scoped theme that aligns the portal's stock Chakra components with the
// rest of the xNotify Customer Portal. The portal uses bare <Heading> and
// <Button> which would otherwise fall back to Chakra defaults (system-ui
// font, 30 px bold headings, gray-ghost buttons). Here we:
//   • Pull headings onto Open Sans (the App's body font) at xNotify weights/sizes.
//   • Set <Button> primary colorScheme to "blue" so the Create-link CTA
//     reads as a primary action, matching the rest of the portal.
//   • Match xNotify's solid pill style for status / purpose badges.
const smartLinksTheme = extendTheme({
    fonts: {
        heading: '"Open Sans", sans-serif',
        body: '"Open Sans", sans-serif',
    },
    colors: {
        brand: {
            50:  'var(--brand-50)', 100: 'var(--brand-100)', 200: 'var(--brand-200)', 300: 'var(--brand-300)',
            400: 'var(--brand-400)', 500: 'var(--brand-primary-hover)', 600: 'var(--brand-primary-active)', 700: 'var(--brand-800)',
            800: 'var(--brand-900)', 900: 'var(--brand-900)',
        },
    },
    components: {
        Heading: {
            baseStyle: { fontWeight: 600, color: 'gray.900', fontFamily: '"Open Sans", sans-serif' },
            sizes: {
                lg: { fontSize: '20px', lineHeight: '28px' },   // page H1
                md: { fontSize: '18px', lineHeight: '26px' },   // detail H1
                sm: { fontSize: '15px', lineHeight: '22px' },
                xs: { fontSize: '13px', lineHeight: '20px' },
            },
        },
        // Canonical xNotify primary button: Chakra colorScheme="blue" — var(--brand-primary) /
        // var(--brand-primary-hover) hover / var(--brand-primary-active) active (blue.500/600/700), matching the app's
        // colorScheme="blue" buttons. Theme-level so every Smart Links <Button>
        // inherits it — never hardcode bg= on individual buttons.
        Button: {
            defaultProps: { size: 'sm', variant: 'solid' },
            variants: {
                solid: {
                    bg: 'var(--brand-primary)',
                    color: 'white',
                    _hover: { bg: 'var(--brand-primary-hover)', _disabled: { bg: 'var(--brand-primary)' } },
                    _active: { bg: 'var(--brand-primary-active)' },
                },
                outline: {
                    borderColor: 'var(--brand-primary)',
                    color: 'var(--brand-primary)',
                    _hover: { bg: 'var(--brand-tint)' },
                },
                ghost: {
                    color: 'gray.700',
                    _hover: { bg: 'gray.100' },
                },
            },
        },
        Badge: {
            baseStyle: {
                px: 2, py: '2px', borderRadius: 'md', fontWeight: 600,
                textTransform: 'capitalize', fontSize: '11px', letterSpacing: '0.02em',
            },
        },
    },
    styles: {
        global: {
            // Tighten the table headers to the xNotify pattern
            'table thead th': {
                fontSize: '11px',
                fontWeight: 600,
                color: 'gray.600',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
            },
        },
    },
});

export default function SmartLinks() {
    const { loading: featurePermissionsLoading, can } = usePagePermissions('marketing/smart-links');

    // Page-level gate is specifically smart-link-list now (was hasAny across
    // list/create/edit) — create/expire/app-profile-save are each gated on
    // their own specific slug inside the relevant child page instead.
    const smartLinksPermission = can("smart-link-list");
    const smartLinkCreatePermission = can("smart-link-create");
    const smartLinkEditPermission = can("smart-link-edit");

    if (featurePermissionsLoading) {
        return (
            <>
                <Header />
                <Box bg="#f8fafc" minH="calc(100vh - 80px)" px={{ base: 2, md: 8 }} py={6}>
                    <Center h="400px">
                        <Spinner size="xl" color="blue.500" thickness="4px" />
                    </Center>
                </Box>
            </>
        );
    }

    return (
        <>
            <Header />
            <Box bg="#f8fafc" minH="calc(100vh - 80px)" px={{ base: 2, md: 8 }} py={6}>
                {smartLinksPermission ? (
                <Box  mx="auto" bg="white" borderRadius="15px" border="1px solid #0000001a" p={{ base: 4, md: 6 }}>
                    <ChakraProvider theme={smartLinksTheme} resetCSS={false}>
                        <Provider store={smartLinksStore}>
                            <Routes>
                                <Route index element={<LinksListPage createPermission={smartLinkCreatePermission} editPermission={smartLinkEditPermission} />} />
                                <Route path="activity" element={<ActivityPage />} />
                                <Route path="links/:id" element={<LinkDetailPage editPermission={smartLinkEditPermission} />} />
                                <Route path="settings/domain" element={<CustomDomainPanel editPermission={smartLinkEditPermission} />} />
                                <Route path="*" element={<Navigate to="." replace />} />
                            </Routes>
                        </Provider>
                    </ChakraProvider>
                </Box>
                ) : (
                <Center h="400px">
                    <Box textAlign="center">
                        <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
                            No Access
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                            You don't have permission to view this sections.
                        </Text>
                    </Box>
                </Center>
                )}
            </Box>
        </>
    );
}
